import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, num, API } from "../lib/api";
import { GlassCard } from "../components/GlassCard";
import { Download } from "lucide-react";
import { Switch } from "../components/ui/switch";

export default function BalanceSheet() {
  const [data, setData] = useState(null);
  const [hideZeros, setHideZeros] = useState(true);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const { data } = await api.get("/balance-sheet");
    setData(data);
  }, []);

  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("workspace-changed", h);
    return () => window.removeEventListener("workspace-changed", h);
  }, [refresh]);

  const downloadXlsx = async () => {
    const token = localStorage.getItem("access_token");
    const ws = localStorage.getItem("workspace_id");
    const resp = await fetch(`${API}/export/balance-sheet.xlsx`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(ws ? { "X-Workspace": ws } : {}),
      },
    });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "balance-sheet.xlsx";
    a.click();
  };

  if (!data) return <div className="text-center text-stone-400 pt-20">Loading…</div>;

  const diffZero = Math.abs(data.difference) < 0.005;

  return (
    <div className="space-y-8" data-testid="balance-sheet-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-stone-500">Position report</p>
          <h1 className="font-serif text-5xl">Balance Sheet</h1>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
            <Switch
              checked={hideZeros}
              onCheckedChange={setHideZeros}
              data-testid="hide-zeros-toggle"
              className="data-[state=checked]:bg-[color:var(--gold)]"
            />
            Hide zero balances
          </label>
          <button
            className="btn-ghost flex items-center gap-2"
            onClick={downloadXlsx}
            data-testid="bs-export-btn"
          >
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Positives — LEFT: money owed TO me (I need to collect) */}
        <GlassCard className="p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200/60 flex items-center justify-between">
            <div>
              <div className="label-tiny text-emerald-600">Positive</div>
              <div className="font-serif text-xl mt-0.5">
                Accounts to collect
              </div>
            </div>
          </div>
          <div className="divide-y divide-stone-200/50">
            {[...data.positives].sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
              <div
                key={p.id}
                onClick={() => navigate(`/entries?account=${p.id}`)}
                className="balance-row-hover px-6 py-3 flex items-center justify-between cursor-pointer transition-colors"
                data-testid={`bs-pos-${p.id}`}
              >
                <span className="text-sm font-medium truncate">{p.name}</span>
                <span className="font-serif text-[26px] leading-none tabular-nums text-emerald-700">{num(p.balance)}</span>
              </div>
            ))}
            {data.positives.length === 0 && (
              <div className="px-6 py-8 text-center text-stone-400 text-sm">None</div>
            )}
          </div>
          <div className="px-6 py-4 bg-emerald-50/50 border-t border-stone-200/60 flex items-center justify-between">
            <span className="label-tiny">Total positive</span>
            <span
              className="font-serif text-[26px] leading-none tabular-nums text-emerald-700"
              data-testid="bs-total-pos"
            >
              {num(data.total_positive)}
            </span>
          </div>
        </GlassCard>

        {/* Negatives — RIGHT: money I owe (I need to pay) */}
        <GlassCard className="p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200/60 flex items-center justify-between">
            <div>
              <div className="label-tiny text-rose-600">Negative</div>
              <div className="font-serif text-xl mt-0.5">
                Accounts to pay
              </div>
            </div>
          </div>
          <div className="divide-y divide-stone-200/50">
            {[...data.negatives].sort((a, b) => a.name.localeCompare(b.name)).map((n) => (
              <div
                key={n.id}
                onClick={() => navigate(`/entries?account=${n.id}`)}
                className="balance-row-hover px-6 py-3 flex items-center justify-between cursor-pointer transition-colors"
                data-testid={`bs-neg-${n.id}`}
              >
                <span className="text-sm font-medium truncate">{n.name}</span>
                <span className="font-serif text-[26px] leading-none tabular-nums text-rose-700">{num(n.balance)}</span>
              </div>
            ))}
            {data.negatives.length === 0 && (
              <div className="px-6 py-8 text-center text-stone-400 text-sm">None</div>
            )}
          </div>
          <div className="px-6 py-4 bg-rose-50/50 border-t border-stone-200/60 flex items-center justify-between">
            <span className="label-tiny">Total negative</span>
            <span
              className="font-serif text-3xl text-rose-700"
              data-testid="bs-total-neg"
            >
              {num(data.total_negative)}
            </span>
          </div>
        </GlassCard>
      </div>

      {/* Zero balance section (shown when toggle is OFF) */}
      {!hideZeros && data.zeros && data.zeros.length > 0 && (
        <GlassCard className="p-0 overflow-hidden" data-testid="bs-zeros-card">
          <div className="px-6 py-4 border-b border-stone-200/60 flex items-center justify-between">
            <div>
              <div className="label-tiny text-stone-500">Zero balance</div>
              <div className="font-serif text-xl mt-0.5">{data.zeros.length} accounts</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 divide-x divide-stone-200/50">
            {[...data.zeros].sort((a, b) => a.name.localeCompare(b.name)).map((z) => (
              <div
                key={z.id}
                onClick={() => navigate(`/entries?account=${z.id}`)}
                className="balance-row-hover px-6 py-3 flex items-center justify-between cursor-pointer transition-colors"
                data-testid={`bs-zero-${z.id}`}
              >
                <span className="text-sm font-medium truncate">{z.name}</span>
                <span className="font-serif text-lg text-stone-500">{num(0)}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Difference */}
      <GlassCard
        className={`text-center ${diffZero ? "" : "ring-2 ring-red-500/50"}`}
        data-testid="bs-difference-card"
      >
        <div className="label-tiny">Difference (should be zero)</div>
        <div
          className={`font-serif text-6xl mt-3 ${
            diffZero ? "text-emerald-700" : "text-red-600"
          }`}
          data-testid="bs-difference-value"
        >
          {num(data.difference)}
        </div>
        <p className="mt-2 text-sm text-stone-500">
          {diffZero
            ? "Books balance perfectly."
            : "Books do not balance. Investigate opening balances or entries."}
        </p>
      </GlassCard>
    </div>
  );
}
